import {UntypedFormGroup} from '@angular/forms';
import {CustomFormControl} from '../components/controls/CustomFormControl';

interface StoredTemplates {
  [key: string]: FormElement[];
}

export class WebFormTemplate {
  specialMappings: SpecialMapping[];
  templates: StoredTemplates;
  pages: FormPage[];

  constructor(templates: StoredTemplates, pages: FormPage[]) {
    this.templates = templates;
    this.pages = pages;
  }
}

export class SpecialMapping {
  finalName: string;
  format: string;
  fields: string[];
}

export class FormPage {
  title: string;
  visibility: FormPageVisibility;
  hints: FormHint[];
  elements: FormElement[];
  specialMappings: SpecialMapping[];
  type: string;
  // These properties are initialized at runtime
  formGroup: UntypedFormGroup;
  controls: CustomFormControl[];
  hasReturnBack: boolean;
  hasNavButtons: boolean;

  constructor(title: string, fields: FormElement[]) {
    this.title = title;
    this.elements = fields;
  }
}

export class FormPageVisibility {
  targetControl: string;
  targetValue: any;
}

export class ControlVisibility {
  targetControl: string;
  targetValue: any;
  comparison: string;
}

export class FormControlInteraction {
  disableValue: string;
  equalityType: string;
  resetTargetOnDisable: boolean;
  disableControls: string[];
}

export class FormHint {
  title: string;
  html: string;
  image: string;
  isExpanded: boolean;
}

export class FormRadioOption {
  id: string;
  label: string;
  description: string;
}

export class FormElement {
  type: string;
  label: string;
  id: string;
  regex?: string;
  syncValidators?: string;
  asyncValidators?: string;
  equalityValidator?: FieldEqualityValidation;
  expander?: FieldExpander;
  placeholder: string;
  readOnly: boolean;
  isProfileField: boolean;
  isIgnoredField: boolean;
  tooltip: string;
  isVisible: boolean; // Temporary in-memory variable
  visibility?: ControlVisibility; // Controls can be visible
  // Radio Group control
  options: FormRadioOption[];
  // Checkbox control
  option = '';
  onChange: ChangeActions;
  // Template control
  initialElementCount: number;
  minimum: number;
  maximum: number;
  width: string;
  showAddButton: boolean;
  alwaysShowDeleteButton: boolean;
  // File control
  uniqueFiles: Set<string>;
  allowedExtensions: Set<string>;
  files: File[] = [];
  maxFiles = 5;
  maxTotalSize: number;
  maxIndividualFileSize: number;
  totalFileSize = 0;
  isPii: boolean;
  fileUploadOptional: boolean;
  fileErrors: string[];
  // Conditional patching
  patcher?: FormElementPatcher;
  // Conditional interaction
  interaction: FormControlInteraction;
  // Conditional autofill
  autofillKey: string;
  // Subscriptions for advanced actions
  subscriptionType: string;
  // Button Reset Logic
  resetFields: string[];
  enableFields: string[];
  // Text area
  maxLength: number;
  // Validations only element (does not have inputs)
  validations: Validation[];
  // Control grid only
  grid: FormElement[][]; // Stores the initial structure of the grid
  gridControls: CustomFormControl[][]; // Stores the final parsed controls
  // Styling
  paddingLeft = 0;

  constructor(type: string, label: string, id: string,
              minimum: number = 1, maximum: number = minimum, width: string = '100%') {
    this.type = type;
    this.label = label;
    this.id = id;
    this.placeholder = '';
    this.minimum = minimum;
    this.maximum = maximum;
    this.width = width;
  }
}

export class ChangeActions {
  actions: ChangeAction[];
}

export class ChangeAction {
  targetControlId: string;
  action: string;
}

export class Validation {
  type: string;
  customErrorMessage: string;
  controls: string[];
}

export class FieldEqualityValidation {
  successIf: string;
  targetControlId: string;
  customErrorMessage: string;
}

export class FieldExpander {
  regex: string;
  replacements: string[];
}

export class FormElementPatcher {
  constructor(public type: string,
              public targets: FormElementPatch[],
              public targetTemplate: string,
              public subPatcher: FormElementPatcher) {
  }
}

export class FormElementPatch {
  constructor(public sourceField: string,
              public targetControlId: string,
              public targetControlValue: any,
              public makeTargetReadOnly: boolean) {
  }
}
